import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { Eye, Printer } from 'react-feather';
import {
  Checkbox,
  Pagination,
  Search,
  Select,
  SelectItem,
} from 'carbon-components-react';
import PageLayout from '../../../components/PageLayout';
import PrintOutHead from '../../../components/PrintOutHead';
import { PrintView } from '../../../components/PrintView/print-view.component';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import {
  formatCurrency,
  getQueryString,
  isSysAdmin,
  limitOptions,
} from '../../../utils/common.util';
import { Redirect } from 'react-router-dom';
import { useUrlState } from '../../../utils/useUrlState';
import { useDebounceState } from '../../../utils/useDebounceState';
import PaymentsReportsModal from './PaymentsReportsModal';
import SupplementaryInvoiceModal from './SupplementaryInvoiceModal';

const PERIOD_SEPARATOR = '|';

const initialData = {
  academicYear: '',
  period: '',
  schoolName: '',
  includeTestSchools: '',
  page: 1,
  take: 20,
};

export default function InvoiceStudentCountsReport() {
  // same rule as the /admin page: system administrators only
  if (!isSysAdmin()) return <Redirect to="/" />;
  return <InvoiceStudentCountsContent />;
}

function InvoiceStudentCountsContent() {
  const [isOpen, setIsOpen] = useState('');
  const [query, setQuery] = useUrlState(initialData);
  const [followUpRow, setFollowUpRow] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  // the search box keeps its own text; the debounced value drives the URL,
  // same debounce hook as the sibling reports
  const [searchText, setSearchText] = useState(query.schoolName);
  const [debouncedSearch, setDebouncedSearch] = useDebounceState(
    query.schoolName,
  );
  useEffect(() => {
    const next = (debouncedSearch ?? '').trim();
    if (next !== query.schoolName) {
      updateFilters({ schoolName: next });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const { data: periodData, isLoading: isLoadingPeriods } = useSWR(
    'school/invoice-periods',
  );
  const periods = useMemo(() => periodData?.periods ?? [], [periodData]);

  const [periodStart = '', periodEnd = ''] = query.period
    ? query.period.split(PERIOD_SEPARATOR)
    : [];

  const page = Math.max(Number(query.page) || 1, 1);
  const take = Number(query.take) || 20;

  const reportKey = query.period
    ? `school/invoice-student-counts?${getQueryString({
        periodStart,
        periodEnd,
        schoolName: query.schoolName,
        includeTestSchools: query.includeTestSchools === 'true' ? true : null,
        skip: (page - 1) * take,
        take,
      })}`
    : null;

  const { data, isLoading, error, mutate } = useSWR(reportKey);

  const academicYears = useMemo(
    () => Array.from(new Set(periods.map((p) => p.academic_year_label))),
    [periods],
  );

  const termOptions = useMemo(
    () =>
      periods.filter(
        (p) =>
          !query.academicYear || p.academic_year_label === query.academicYear,
      ),
    [periods, query.academicYear],
  );

  const selectedPeriod = periods.find((p) => periodValue(p) === query.period);
  const termLabel = selectedPeriod ? termName(selectedPeriod) : '';

  const rows = data?.rows ?? [];
  const totalCount = data?.pagination?.total_count ?? 0;

  function updateFilters(changes) {
    setSuccessMessage('');
    setQuery({ ...changes, page: 1 });
  }

  function handleSearch(value) {
    setSearchText(value);
    setDebouncedSearch(value);
  }

  function handleIssued(invoiceNumber, schoolName) {
    setFollowUpRow(null);
    setSuccessMessage(
      `Additional students invoice ${invoiceNumber} has been sent to ${schoolName}.`,
    );
    mutate();
  }

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Admin', to: '/admin' },
        { label: 'Invoice Student Counts', active: true },
      ]}
      title={
        <div
          className="d-flex align-items-center flex-wrap"
          style={{ gap: '30px' }}
        >
          <h1 className="h-20">Invoice Student Counts</h1>
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn"
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('reports')}
          >
            <Eye size="14" strokeWidth="2px" />
            <span>VIEW OTHER REPORTS</span>
          </button>
        </div>
      }
    >
      <p className="mt-20 text-12">
        Students each school was invoiced for in a term, against its current
        enrolment. Select a term to show the report.
      </p>

      {/* FILTERS */}
      <div className="mt-10 card-row-3">
        <Select
          id="academic-year"
          labelText="Academic Year"
          value={query.academicYear}
          onChange={(e) => {
            const academicYear = e.target.value;
            const keepPeriod =
              !academicYear ||
              selectedPeriod?.academic_year_label === academicYear;
            updateFilters({
              academicYear,
              period: keepPeriod ? query.period : '',
            });
          }}
        >
          <SelectItem value="" text="All" />
          {academicYears.map((label) => (
            <SelectItem key={label} value={label} text={label} />
          ))}
        </Select>

        <Select
          id="term"
          labelText="Term (required)"
          value={query.period}
          disabled={isLoadingPeriods}
          onChange={(e) => updateFilters({ period: e.target.value })}
        >
          <SelectItem
            value=""
            text={isLoadingPeriods ? 'Loading terms...' : 'Select a term'}
          />
          {termOptions.map((p) => (
            <SelectItem
              key={periodValue(p)}
              value={periodValue(p)}
              text={`${termName(p)} — ${p.school_count} schools`}
            />
          ))}
        </Select>

        <div style={{ minWidth: '320px', marginTop: '24px' }}>
          <Search
            id="school-search"
            labelText="Search school"
            placeholder="Search school name"
            size="lg"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>

      <div
        className="mt-20 d-flex align-items-center flex-wrap"
        style={{ gap: '30px' }}
      >
        <Checkbox
          id="include-test-schools"
          labelText="Include test schools"
          checked={query.includeTestSchools === 'true'}
          onChange={(value, detail) => {
            const checked =
              typeof value === 'boolean' ? value : Boolean(detail?.checked);
            updateFilters({ includeTestSchools: checked ? 'true' : '' });
          }}
        />
      </div>

      <div className="mt-20">
        {successMessage && (
          <p className="text-14 mt-10 text-success">{successMessage}</p>
        )}
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {data?.enrollment_unavailable && (
          <p className="text-14 mt-10 text-danger">
            Current enrolment could not be retrieved from the platform. Enrolled
            now, growth and unbilled are blank until it is available.
          </p>
        )}

        {!query.period && (
          <p className="text-14 mt-30 text-grey">
            Select a term to see invoiced and current student counts.
          </p>
        )}

        {query.period && isLoading && (
          <p className="text-14 mt-30 text-grey">Loading report...</p>
        )}

        {query.period && !isLoading && rows.length === 0 && !error && (
          <p className="text-14 mt-30 text-grey">No data available.</p>
        )}

        {rows.length > 0 && (
          <>
            <PrintOutHead
              title="Invoice Student Counts"
              headings={[
                { label: 'Term', value: termLabel },
                {
                  label: 'Academic Year',
                  value: selectedPeriod?.academic_year_label ?? '',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                <table className="bx--data-table table-white">
                  <thead>
                    <tr>
                      <th>School</th>
                      <th>Invoice #</th>
                      <th>Status</th>
                      <th>Students At Invoice</th>
                      <th>Enrolled Now</th>
                      <th>Growth Since Invoice</th>
                      <th>Billed In Follow-ups</th>
                      <th>Unbilled</th>
                      <th>Unit Price (GH₵)</th>
                      <th>Amount (GH₵)</th>
                      <th className="no-print">Follow-up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.epobs_account_id}>
                        <td className="font-bold">{row.school_name}</td>
                        <td>{row.general_invoice_number}</td>
                        <td>{row.general_invoice_status}</td>
                        <td>{formatCount(row.students_at_invoice)}</td>
                        <td>{formatCount(row.enrolled_now)}</td>
                        <td>{formatSigned(row.growth_since_invoice)}</td>
                        <td>{formatCount(row.supplementary_billed)}</td>
                        <td
                          className={
                            row.unbilled > 0 ? 'font-bold text-danger' : ''
                          }
                        >
                          {formatCount(row.unbilled)}
                        </td>
                        <td>{formatMoney(row.unit_price)}</td>
                        <td>{formatMoney(row.general_invoice_amount_due)}</td>
                        <td className="no-print">
                          <FollowUpCell
                            row={row}
                            onClick={() => {
                              setSuccessMessage('');
                              setFollowUpRow(row);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </PrintView>
            <Pagination
              backwardText="Previous page"
              forwardText="Next page"
              page={page}
              pageNumberText="Page Number"
              pageSize={take}
              pageSizes={limitOptions(totalCount)}
              totalItems={totalCount}
              onChange={(value) =>
                setQuery({ page: value.page, take: value.pageSize })
              }
            />
          </>
        )}

        {rows.length > 0 && (
          <div
            className="mt-30"
            style={{
              display: 'flex',
              justifySelf: 'end',
              gap: '20px',
              alignItems: 'center',
            }}
          >
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>

      <SupplementaryInvoiceModal
        isOpen={!!followUpRow}
        row={followUpRow}
        periodStart={periodStart}
        periodEnd={periodEnd}
        termLabel={termLabel}
        closeModal={() => setFollowUpRow(null)}
        onIssued={handleIssued}
      />

      <PaymentsReportsModal
        isOpen={isOpen === 'reports'}
        closeModal={() => setIsOpen('')}
      />
    </PageLayout>
  );
}

function FollowUpCell({ row, onClick }) {
  const eligible =
    row.can_issue_supplementary &&
    row.plan_type === 'PER_STUDENT' &&
    row.general_invoice_status !== 'VOIDED' &&
    (row.unbilled ?? 0) > 0;

  // always shown; disabled the same way as the attendance page's SUBMIT button
  // (primary button + disabled). The wrapper carries the reason because disabled
  // buttons swallow hover events.
  return (
    <span
      title={eligible ? '' : row.supplementary_blocked_reason ?? ''}
      style={{ display: 'inline-block' }}
    >
      <CustomButton
        classes="primary small font-bold"
        disabled={!eligible}
        onClick={onClick}
      >
        {eligible ? `BILL ${row.unbilled}` : 'BILL'}
      </CustomButton>
    </span>
  );
}

function periodValue(period) {
  return `${period.period_start}${PERIOD_SEPARATOR}${period.period_end}`;
}

function termName(period) {
  return `${period.term_label} (${period.academic_year_label})`;
}

function formatCount(value) {
  return value === null || value === undefined
    ? '—'
    : Number(value).toLocaleString('en-GH');
}

function formatSigned(value) {
  if (value === null || value === undefined) return '—';
  if (value > 0) return `+${value.toLocaleString('en-GH')}`;
  if (value < 0) return `−${Math.abs(value).toLocaleString('en-GH')}`;
  return '0';
}

function formatMoney(value) {
  return value === null || value === undefined
    ? '—'
    : formatCurrency(Number(value));
}
